import {SoloDiscoveryExperience} from '@/components/leadershipInsights/SoloDiscoveryExperience';
import {useUserSettings} from '@/hooks/useUserSettings';
import {Button} from '@/components/ui/button';
import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useLivingConstellationState } from "@/hooks/useLivingConstellationState";
import { getNodeVisualSpec, type BehaviorConstellationState, type CoreDomain } from "@/lib/livingConstellation";
import { LivingConstellationDetailPanel } from "./LivingConstellationDetailPanel";

const CORE_DOMAINS: CoreDomain[] = ["CONNECT", "OWN", "RESPOND", "ELEVATE"];

const DOMAIN_STYLES: Record<CoreDomain, { panel: string; label: string }> = {
  CONNECT: {
    panel: "border-[#d8c7ae]/80 from-[#b86b3b]/[0.10] via-transparent to-transparent dark:border-[#d8c7ae]/18",
    label: "text-[#7f4a2c] dark:text-[#f4bf92]",
  },
  OWN: {
    panel: "border-[#d8c7ae]/80 from-[#d9b16e]/[0.14] via-transparent to-transparent dark:border-[#d8c7ae]/18",
    label: "text-[#7a5520] dark:text-[#f2d39a]",
  },
  RESPOND: {
    panel: "border-[#d7cce6]/80 from-[#9b7ac5]/[0.12] via-transparent to-transparent dark:border-[#d7cce6]/20",
    label: "text-[#5f4b7a] dark:text-[#d8c7f2]",
  },
  ELEVATE: {
    panel: "border-[#d8c7ae]/80 from-[#6f4f8f]/[0.10] via-transparent to-transparent dark:border-[#d8c7ae]/18",
    label: "text-[#5d416f] dark:text-[#d9c3e8]",
  },
};

function domainAtmosphere(nodes: BehaviorConstellationState[]): number {
  const strengtheningCount = nodes.filter((node) => node.state === "strengthening").length;
  const mixedCount = nodes.filter((node) => node.state === "mixed").length;
  return Math.min(0.28, strengtheningCount * 0.08 + mixedCount * 0.03);
}

function getConstellationMaturity(states: BehaviorConstellationState[]) {
  const observedMeetings = new Set(states.flatMap((node) => node.evidenceMeetingIds)).size;
  const strengtheningCount = states.filter((node) => node.state === "strengthening").length;
  const mixedCount = states.filter((node) => node.state === "mixed" || node.state === "receding").length;

  return {
    observedMeetings,
    headline:
      observedMeetings >= 3
        ? `Observed across ${observedMeetings} meeting${observedMeetings === 1 ? "" : "s"}`
        : observedMeetings > 0
        ? `Early read from ${observedMeetings} meeting${observedMeetings === 1 ? "" : "s"}`
        : "Waiting for observed meetings",
    narrative:
      observedMeetings >= 3
        ? "Coach Veera is now grounding this map in repeated meeting evidence."
        : observedMeetings > 0
        ? "This map is directional. More real meetings will make the signals more trustworthy."
        : "Upload a transcript, add context, and keep bringing real meetings into view so behaviors can be observed.",
    status:
      strengtheningCount > 0
        ? `${strengtheningCount} behavior${strengtheningCount === 1 ? "" : "s"} currently stand out most clearly.`
        : mixedCount > 0
        ? "Current signals show counter-pressure as well as support."
        : "No durable recurring pattern is visible yet.",
  };
}

function getSourceBadgeLabel(source: "persisted_constellation" | "fallback_practice_summary" | "degraded_missing_tables" | null) {
  if (source === "persisted_constellation") return "Observed behavioral signals";
  if (source === "fallback_practice_summary") return "Early transcript read";
  if (source === "degraded_missing_tables") return "Degraded fallback";
  return "Loading source";
}

function LivingNode({
  node,
  isSelected,
  onSelect,
}: {
  node: BehaviorConstellationState;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const visual = getNodeVisualSpec(node.state, node.netIntensity);
  return (
    <div className="relative group">
      <span
        className="pointer-events-none absolute inset-0 rounded-full"
        style={{
          transform: "scale(1.55)",
          opacity: visual.auraOpacity,
          filter: "blur(8px)",
          background: "currentColor",
        }}
      />
      <button
        onClick={onSelect}
        className={cn(
          "relative h-10 w-10 rounded-full border-2 transition-all duration-300 hover:scale-105",
          visual.outerClass,
          isSelected && "ring-2 ring-[#b86b3b]/50 ring-offset-2 ring-offset-background scale-110"
        )}
        title={`${node.behaviorName}: ${node.state}`}
        aria-label={`${node.behaviorName}: ${node.state}`}
        style={{
          opacity: 0.75 + (node.netIntensity * 0.25),
          filter: `saturate(${visual.saturation})`,
        }}
      >
        <span className={cn("absolute inset-[7px] rounded-full", visual.innerClass)} />
      </button>
      <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-popover px-2 py-1 text-xs text-popover-foreground shadow opacity-0 transition-opacity group-hover:opacity-100">
        {node.behaviorName}
      </span>
    </div>
  );
}

function DomainCluster({
  domain,
  nodes,
  selectedBehaviorId,
  onSelectBehavior,
}: {
  domain: CoreDomain;
  nodes: BehaviorConstellationState[];
  selectedBehaviorId: string | null;
  onSelectBehavior: (behaviorId: string) => void;
}) {
  const styles = DOMAIN_STYLES[domain];
  const atmosphere = domainAtmosphere(nodes);

  return (
    <div
      className={cn(
        "rounded-2xl border bg-gradient-to-br bg-white/70 p-6 transition-all dark:bg-white/[0.04]",
        styles.panel
      )}
      style={{ boxShadow: `0 0 0 1px rgba(255,255,255,0.02), 0 18px 45px rgba(0,0,0,${atmosphere})` }}
    >
      <div className="mb-4 text-center">
        <h3 className={cn("text-sm font-semibold tracking-wide", styles.label)}>{domain}</h3>
      </div>
      <div className="relative">
        <div className="absolute left-4 right-4 top-5 h-px bg-border/40" />
        <div className="relative z-10 flex flex-wrap justify-center gap-4">
          {nodes.map((node) => (
            <LivingNode
              key={node.behaviorId}
              node={node}
              isSelected={selectedBehaviorId === node.behaviorId}
              onSelect={() => onSelectBehavior(node.behaviorId)}
            />
          ))}
        </div>
      </div>
      <p className="mt-3 text-center text-[10px] text-muted-foreground">
        {nodes.length > 0 ? `${nodes.length} behavior${nodes.length === 1 ? "" : "s"} visible. Tap a circle to inspect evidence.` : "No active behaviors surfaced here yet."}
      </p>
    </div>
  );
}

interface LivingBehaviorConstellationProps {
  leaderId?: string;
}

function OwnerConstellationDiscovery({context,onRefresh}:React.ComponentProps<typeof SoloDiscoveryExperience>) {
  const {data:settings}=useUserSettings();
  return <SoloDiscoveryExperience context={context} lensId={settings?.leadershipLens} onRefresh={onRefresh} compact/>;
}

export function LivingBehaviorConstellation({ leaderId }: LivingBehaviorConstellationProps = {}) {
  const { user } = useAuth();
  const targetLeaderId = leaderId || user?.id;
  const [selectedBehaviorId, setSelectedBehaviorId] = useState<string | null>(null);
  const { states, groupedByTheme, source, isLoading, error, refetch, governedBi } = useLivingConstellationState(targetLeaderId);
  const maturity = useMemo(() => getConstellationMaturity(states), [states]);

  const selectedNode = useMemo(() => {
    if (!selectedBehaviorId) return null;
    return states.find((node) => node.behaviorId === selectedBehaviorId) || null;
  }, [states, selectedBehaviorId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || (governedBi?.status==='unavailable' && ['query_failed','invalid_payload'].includes(governedBi.reason))) return <div role="alert">Behavioral observation is unavailable. <Button variant="outline" onClick={()=>void refetch()}>Retry behavioral observation</Button></div>;
  if (governedBi?.ownerProjection) return <OwnerConstellationDiscovery context={governedBi} onRefresh={refetch}/>;

  if (states.length === 0) {
    return (
      <div className="space-y-4 rounded-2xl border border-dashed border-[#d8c7ae]/70 bg-[#fffdf7]/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/[0.04]">
        <div className="space-y-2">
          <h2 className="font-serif text-h3 text-[#251837] dark:text-[#f7f0e8]">Your behavior map</h2>
          <p className="text-small text-muted-foreground">
            Coach Veera is still building this map from observed meetings, context, and repeated behavioral signals.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline" className="text-[11px]">{getSourceBadgeLabel(source)}</Badge>
          <Badge variant="outline" className="text-[11px]">Best after 3 to 5 meetings</Badge>
        </div>
        <p className="text-sm text-foreground/90">
          Evidence paths: reviewed meeting records, profile context, and coach-led conversations will make this map easier to interpret.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-h3 text-[#251837] dark:text-[#f7f0e8]">Your behavior map</h2>
        <p className="mt-1 text-small text-muted-foreground">
          Each node brightens or softens as observed behaviors recur across meetings. Tap one to see the evidence, source, and current read.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Badge variant="outline" className="text-[11px]">{maturity.headline}</Badge>
          <Badge variant="outline" className="text-[11px]">{getSourceBadgeLabel(source)}</Badge>
        </div>
        <p className="mt-3 text-sm text-foreground/90">{maturity.narrative}</p>
        <p className="mt-1 text-xs text-muted-foreground">{maturity.status}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {CORE_DOMAINS.map((domain) => (
          <DomainCluster
            key={domain}
            domain={domain}
            nodes={groupedByTheme[domain]}
            selectedBehaviorId={selectedBehaviorId}
            onSelectBehavior={setSelectedBehaviorId}
          />
        ))}
      </div>

      {selectedNode && (
        <div className="animate-fade-in space-y-2">
          <p className="text-xs text-muted-foreground">
            {selectedNode.behaviorName} in {selectedNode.theme}
          </p>
          <LivingConstellationDetailPanel node={selectedNode} />
        </div>
      )}
    </div>
  );
}
